import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { apiFetch } from '../../services/api';

/**
 * ConversionPreviewBanner displays lossy conversion warnings and errors.
 *
 * Features:
 * - Color-coded severity badges (INFO=blue, WARNING=yellow, ERROR=red)
 * - Collapsible sections per severity
 * - Expandable detail lists grouped by field
 * - Per-item chip display for affectedTasks
 * - Mitigation text in italic
 *
 * Props:
 *   report: ConversionReport JSON object
 *   onCommit: callback when user clicks Commit button (placeholder)
 */
export const ConversionPreviewBanner = ({ report, onCommit, discoveredWorkflowId, onNavigate }) => {
  const [expandedSeverities, setExpandedSeverities] = useState({
    INFO: false,
    WARNING: true,
    ERROR: true,
  });
  const [expandedItems, setExpandedItems] = useState({});
  const [isCommitting, setIsCommitting] = useState(false);
  const [commitError, setCommitError] = useState(null);

  if (!report) return null;

  const lossyItems = report.lossyItems || [];
  const warnings = report.warnings || [];

  // Combine and group by severity
  const allItems = [...lossyItems, ...warnings];
  const itemsBySeverity = {
    INFO: allItems.filter((w) => w.severity === 'INFO'),
    WARNING: allItems.filter((w) => w.severity === 'WARNING'),
    ERROR: allItems.filter((w) => w.severity === 'ERROR'),
  };

  const toggleSeverity = (severity) => {
    setExpandedSeverities((prev) => ({
      ...prev,
      [severity]: !prev[severity],
    }));
  };

  const toggleItemExpanded = (itemId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const handleCommit = async () => {
    if (!discoveredWorkflowId) {
      setCommitError('Missing discovered workflow ID');
      return;
    }

    setIsCommitting(true);
    setCommitError(null);

    try {
      // Routed through the shared apiFetch so this mutating POST carries auth +
      // the CSRF double-submit header + cookie and the single-flight token
      // refresh (no hand-rolled / dead-localStorage bearer). apiFetch throws on
      // non-OK with the server error message.
      const res = await apiFetch(`/api/v1/discovery/workflows/${discoveredWorkflowId}/convert-native`, 'POST');
      const result = res.data || {};

      if (result.success && result.nativeWorkflowId) {
        // Navigate to the newly created workflow detail page
        if (onNavigate) {
          onNavigate(`/workflows/${result.nativeWorkflowId}`);
        } else {
          window.location.href = `/workflows/${result.nativeWorkflowId}`;
        }
      } else {
        throw new Error(result.error || 'Commit succeeded but no workflow ID returned');
      }
    } catch (error) {
      console.error('Commit error:', error);
      setCommitError(error.message || 'Failed to commit conversion');
    } finally {
      setIsCommitting(false);
    }
  };

  const severityConfig = {
    INFO: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      badge: 'bg-blue-100 text-blue-800',
      text: 'text-blue-900',
    },
    WARNING: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      badge: 'bg-yellow-100 text-yellow-800',
      text: 'text-yellow-900',
    },
    ERROR: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      badge: 'bg-red-100 text-red-800',
      text: 'text-red-900',
    },
  };

  const renderSeveritySection = (severity, items) => {
    if (items.length === 0) return null;

    const config = severityConfig[severity];
    const isExpanded = expandedSeverities[severity];

    return (
      <div
        key={severity}
        className={`border ${config.border} ${config.bg} rounded-lg p-3 mb-3`}
      >
        <button
          onClick={() => toggleSeverity(severity)}
          className={`flex w-full items-center justify-between ${config.text} font-semibold`}
        >
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded text-xs font-bold ${config.badge}`}>
              {severity}
            </span>
            <span>{items.length} item(s)</span>
          </div>
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {isExpanded && (
          <div className="mt-2 space-y-2">
            {items.map((item, idx) => (
              <div
                key={`${severity}-${idx}`}
                className="bg-white border border-gray-200 rounded p-2"
              >
                <button
                  onClick={() => toggleItemExpanded(`${severity}-${idx}`)}
                  className="flex w-full items-center justify-between text-sm font-medium text-gray-900"
                >
                  <div className="flex flex-col items-start gap-1">
                    <span>{item.field || item.category}</span>
                    {item.message && (
                      <span className="text-xs text-gray-600">{item.message}</span>
                    )}
                  </div>
                  {expandedItems[`${severity}-${idx}`] ? (
                    <ChevronUp size={14} />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                </button>

                {expandedItems[`${severity}-${idx}`] && (
                  <div className="mt-2 text-xs space-y-2">
                    {item.affectedTasks && item.affectedTasks.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {item.affectedTasks.map((task) => (
                          <span
                            key={task}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 border border-gray-300"
                          >
                            {task}
                          </span>
                        ))}
                      </div>
                    )}
                    {item.mitigation && (
                      <p className="text-gray-700 italic">{item.mitigation}</p>
                    )}
                    {item.suggestion && (
                      <p className="text-gray-700">{item.suggestion}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full bg-gray-50 border-t border-b border-gray-200 p-4">
      <div className="max-w-7xl mx-auto">
        <h3 className="text-sm font-bold text-gray-900 mb-3">Conversion Preview</h3>

        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="bg-white rounded p-2 text-center">
            <div className="text-xs text-gray-600">Source Tasks</div>
            <div className="text-lg font-bold text-gray-900">
              {report.totalSourceTasks}
            </div>
          </div>
          <div className="bg-white rounded p-2 text-center">
            <div className="text-xs text-gray-600">Canvas Nodes</div>
            <div className="text-lg font-bold text-gray-900">
              {report.totalCanvasNodes}
            </div>
          </div>
          <div className="bg-white rounded p-2 text-center">
            <div className="text-xs text-gray-600">Connections</div>
            <div className="text-lg font-bold text-gray-900">
              {report.totalConnections}
            </div>
          </div>
          <div className="bg-white rounded p-2 text-center">
            <div className="text-xs text-gray-600">Issues</div>
            <div className="text-lg font-bold text-gray-900">
              {allItems.length}
            </div>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          {renderSeveritySection('ERROR', itemsBySeverity.ERROR)}
          {renderSeveritySection('WARNING', itemsBySeverity.WARNING)}
          {renderSeveritySection('INFO', itemsBySeverity.INFO)}
        </div>

        <div className="flex gap-2">
          {commitError && (
            <div className="flex-1 bg-red-100 border border-red-300 text-red-800 px-3 py-2 rounded text-sm">
              {commitError}
            </div>
          )}
          <button
            onClick={() => handleCommit()}
            disabled={isCommitting}
            className={`px-4 py-2 rounded font-medium text-sm flex items-center gap-2 ${
              isCommitting
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {isCommitting ? (
              <>
                <span className="inline-block animate-spin">&#8987;</span>
                Committing...
              </>
            ) : (
              'Commit'
            )}
          </button>
          <button
            onClick={() => window.history.back()}
            disabled={isCommitting}
            className="px-4 py-2 bg-gray-300 text-gray-900 rounded font-medium text-sm hover:bg-gray-400 disabled:bg-gray-200 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConversionPreviewBanner;
